import type * as React from 'react'
import { cn } from '../lib/cn'

export interface LaneItem {
  id: string
  label: string
  href: string
  icon?: React.ReactNode
  /** Live count; `'unknown'` = the count failed to load (renders "–", never a guessed number). */
  count?: number | 'unknown'
  /** #876 cases in the lane with a reply this person has not seen: "17 · 3 new". */
  unread?: number
  /** Count rendered red (overdue lanes). */
  alert?: boolean
  keys?: string
}

export interface LaneSection {
  id: string
  label?: string
  items: LaneItem[]
}

export type LinkRenderer = (props: {
  href: string
  className: string
  children: React.ReactNode
  'aria-current'?: 'page'
  title?: string
  'data-slot'?: string
}) => React.ReactNode

const defaultLink: LinkRenderer = ({ children, ...props }) => <a {...props}>{children}</a>

/**
 * Left pane: lanes with live counts, grouped (Work · Ledger · Workspace). Router-agnostic: pass
 * `renderLink` to use the app's router link.
 */
export function LaneNav({
  sections,
  activeId,
  renderLink = defaultLink,
  footer,
}: {
  sections: LaneSection[]
  activeId?: string
  renderLink?: LinkRenderer
  footer?: React.ReactNode
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto px-2 py-3">
        {sections.map((section) => (
          <div key={section.id}>
            {section.label ? <p className="px-2.5 pb-1 text-xs font-medium text-subtle">{section.label}</p> : null}
            <ul className="space-y-px">
              {section.items.map((item) => {
                const active = item.id === activeId
                return (
                  <li key={item.id}>
                    {renderLink({
                      href: item.href,
                      'data-slot': 'lane-link',
                      'aria-current': active ? 'page' : undefined,
                      // Fully visible desktop labels need no native tooltip. Those browser-owned popups can linger
                      // over the queue after the pointer leaves the nav (#715). Keep only shortcut hints.
                      title: item.keys ? `${item.label} (${item.keys})` : undefined,
                      className: cn(
                        'relative flex h-8 items-center gap-2.5 rounded-md px-2.5 text-sm text-foreground/85 no-underline transition-colors hover:bg-muted max-[899px]:h-11 [&_svg]:size-4 [&_svg]:text-muted-foreground',
                        active &&
                          'bg-accent font-medium text-accent-foreground [&_svg]:text-primary before:absolute before:inset-y-1.5 before:-left-2 before:w-[3px] before:rounded-r before:bg-primary',
                      ),
                      children: (
                        <>
                          {item.icon}
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.count === 'unknown' ? (
                            <span className="tabular text-xs text-subtle" title="Couldn't load this count">
                              –
                            </span>
                          ) : item.count !== undefined ? (
                            <span
                              className={cn(
                                'tabular text-xs',
                                item.alert && item.count > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground',
                              )}
                            >
                              {item.count}
                              {item.unread ? (
                                <span className="font-semibold text-primary" data-slot="lane-unread">
                                  {' · '}
                                  {item.unread} new
                                </span>
                              ) : null}
                            </span>
                          ) : null}
                        </>
                      ),
                    })}
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
      {footer ? <div className="border-t border-border p-2">{footer}</div> : null}
    </div>
  )
}
